import React from 'react';
import { ProfileAvatar } from '../ui';
import type { Conversation } from '../../store/messageStore';

interface ConversationListProps {
  conversations: Conversation[];
  activeUserId: string | null;
  onConversationSelect: (userId: string) => void;
  isLoading?: boolean;
}

const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeUserId,
  onConversationSelect,
  isLoading = false
}) => {
  const formatTime = (dateString: string) => {
    const messageDate = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - messageDate.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    // Show relative time for recent messages
    if (diffInMinutes < 1) {
      return 'nu';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d`;
    } else {
      // For older messages, show the actual time
      return messageDate.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon-cyan"></div>
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center py-8 px-4 text-fg-muted">
        <div className="text-lg font-medium mb-2 text-fg-soft">Nog geen gesprekken</div>
        <div className="text-sm italic">
          Start een gesprek door op de berichtknop te klikken bij een gebruikersprofiel.
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 space-y-1">
      {conversations.map((conversation) => (
        <div
          key={conversation.user.id}
          onClick={() => onConversationSelect(conversation.user.id)}
          className={`
            sf-item flex items-center p-3 cursor-pointer transition-colors border
            ${activeUserId === conversation.user.id
              ? 'bg-neon-gradient-soft border-neon-pink/[.32]'
              : 'border-transparent hover:bg-white/[.07]'}
          `}
        >
          <div className="relative">
            <ProfileAvatar user={conversation.user} size="md" />
            {conversation.hasUnread && (
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-neon-cyan shadow-neon-cyan rounded-full border-2 border-surface"></div>
            )}
          </div>

          <div className="ml-3 flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <p className="truncate font-semibold text-fg-bright">
                {conversation.user.username}
              </p>
              {conversation.lastMessage && (
                <span className="font-mono text-[11px] text-fg-meta ml-2 flex-shrink-0">
                  {formatTime(conversation.lastMessage.sentAt)}
                </span>
              )}
            </div>

            {conversation.lastMessage && (
              <p className={`text-sm truncate mt-1 ${conversation.hasUnread ? 'text-fg-soft font-medium' : 'text-fg-muted'}`}>
                {conversation.lastMessage.message || 'GIF'}
              </p>
            )}

            {!conversation.lastMessage && (
              <p className="text-sm text-fg-muted mt-1 italic">
                Gesprek gestart
              </p>
            )}
          </div>

          {conversation.hasUnread && (
            <div className="ml-2 w-2 h-2 bg-neon-cyan shadow-neon-cyan rounded-full flex-shrink-0"></div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ConversationList;